import { supabase } from '../utils/supabase';

/**
 * Booking Service
 * Handles all booking and reservation operations
 */
class BookingService {
  /**
   * Create a new booking
   * @param {Object} bookingData - Booking information
   * @returns {Promise<Object>} Result with created booking
   */
  async createBooking(bookingData) {
    try {
      const {
        vendorId,
        userId,
        inquiryId,
        packageId,
        eventDate,
        eventTime,
        eventLocation,
        guestCount,
        totalAmount,
        specialRequirements,
        eventType
      } = bookingData;

      // Validate required fields
      if (!vendorId || !userId || !eventDate) {
        return {
          success: false,
          error: 'Vendor ID, User ID, and Event Date are required'
        };
      }

      // Check if vendor exists
      const { data: vendor, error: vendorError } = await supabase
        .from('vendor_profiles')
        .select('id, business_name')
        .eq('id', vendorId)
        .single();

      if (vendorError || !vendor) {
        return {
          success: false,
          error: 'Vendor not found'
        };
      }

      // Create booking
      const { data, error } = await supabase
        .from('bookings')
        .insert([{
          vendor_id: vendorId,
          user_id: userId,
          inquiry_id: inquiryId || null,
          package_id: packageId || null,
          event_date: eventDate,
          event_time: eventTime || null,
          event_location: eventLocation,
          guest_count: guestCount || null,
          total_amount: totalAmount,
          payment_status: 'pending',
          status: 'pending',
          special_requirements: specialRequirements || null,
          event_type: eventType || 'wedding'
        }])
        .select(`
          *,
          vendor:vendor_profiles!vendor_id (
            id,
            business_name,
            category,
            city,
            state
          ),
          user:user_profiles!user_id (
            id,
            full_name,
            email,
            phone
          ),
          package:vendor_packages!package_id (
            id,
            name,
            price,
            description
          )
        `)
        .single();

      if (error) {
        console.error('Error creating booking:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error in createBooking:', error);
      return { success: false, error: 'Failed to create booking' };
    }
  }

  /**
   * Get booking by ID
   * @param {string} bookingId - Booking UUID
   * @returns {Promise<Object>} Result with booking details
   */
  async getBookingById(bookingId) {
    try {
      const { data, error } = await supabase
        .from('bookings')
        .select(`
          *,
          vendor:vendor_profiles!vendor_id (
            id,
            business_name,
            category,
            city,
            state,
            phone,
            email
          ),
          user:user_profiles!user_id (
            id,
            full_name,
            email,
            phone
          ),
          package:vendor_packages!package_id (
            id,
            name,
            price,
            description,
            features
          ),
          inquiry:inquiries!inquiry_id (
            id,
            message,
            budget
          )
        `)
        .eq('id', bookingId)
        .single();

      if (error) {
        console.error('Error fetching booking:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error in getBookingById:', error);
      return { success: false, error: 'Failed to fetch booking' };
    }
  }

  /**
   * Get all bookings for a user
   * @param {string} userId - User UUID
   * @param {Object} filters - Optional filters
   * @returns {Promise<Object>} Result with bookings array
   */
  async getUserBookings(userId, filters = {}) {
    try {
      let query = supabase
        .from('bookings')
        .select(`
          *,
          vendor:vendor_profiles!vendor_id (
            id,
            business_name,
            category,
            city,
            state
          ),
          package:vendor_packages!package_id (
            id,
            name,
            price
          )
        `, { count: 'exact' })
        .eq('user_id', userId);

      // Apply status filter
      if (filters.status) {
        query = query.eq('status', filters.status);
      }

      // Apply date range filter
      if (filters.startDate) {
        query = query.gte('event_date', filters.startDate);
      }

      if (filters.endDate) {
        query = query.lte('event_date', filters.endDate);
      }

      // Sorting
      const sortBy = filters.sortBy || 'event_date';
      const sortOrder = filters.sortOrder === 'asc';
      query = query.order(sortBy, { ascending: sortOrder });

      // Pagination
      if (filters.page && filters.limit) {
        const from = (filters.page - 1) * filters.limit;
        const to = from + filters.limit - 1;
        query = query.range(from, to);
      }

      const { data, error, count } = await query;

      if (error) {
        console.error('Error fetching user bookings:', error);
        return { success: false, error: error.message };
      }

      return {
        success: true,
        data: data || [],
        count
      };
    } catch (error) {
      console.error('Error in getUserBookings:', error);
      return { success: false, error: 'Failed to fetch user bookings' };
    }
  }

  /**
   * Get all bookings for a vendor
   * @param {string} vendorId - Vendor UUID
   * @param {Object} filters - Optional filters
   * @returns {Promise<Object>} Result with bookings array
   */
  async getVendorBookings(vendorId, filters = {}) {
    try {
      let query = supabase
        .from('bookings')
        .select(`
          *,
          user:user_profiles!user_id (
            id,
            full_name,
            email,
            phone
          ),
          package:vendor_packages!package_id (
            id,
            name,
            price
          )
        `, { count: 'exact' })
        .eq('vendor_id', vendorId);

      // Apply status filter
      if (filters.status) {
        query = query.eq('status', filters.status);
      }

      // Apply date range filter
      if (filters.startDate) {
        query = query.gte('event_date', filters.startDate);
      }

      if (filters.endDate) {
        query = query.lte('event_date', filters.endDate);
      }

      // Sorting
      const sortBy = filters.sortBy || 'event_date';
      const sortOrder = filters.sortOrder === 'asc';
      query = query.order(sortBy, { ascending: sortOrder });

      // Pagination
      if (filters.page && filters.limit) {
        const from = (filters.page - 1) * filters.limit;
        const to = from + filters.limit - 1;
        query = query.range(from, to);
      }

      const { data, error, count } = await query;

      if (error) {
        console.error('Error fetching vendor bookings:', error);
        return { success: false, error: error.message };
      }

      return {
        success: true,
        data: data || [],
        count
      };
    } catch (error) {
      console.error('Error in getVendorBookings:', error);
      return { success: false, error: 'Failed to fetch vendor bookings' };
    }
  }

  /**
   * Update booking status
   * @param {string} bookingId - Booking UUID
   * @param {string} status - New status
   * @param {string} userId - User making the update
   * @returns {Promise<Object>} Result with updated booking
   */
  async updateBookingStatus(bookingId, status, userId) {
    try {
      // Valid statuses: pending, confirmed, completed, cancelled, rejected
      const validStatuses = ['pending', 'confirmed', 'completed', 'cancelled', 'rejected'];
      
      if (!validStatuses.includes(status)) {
        return {
          success: false,
          error: 'Invalid status value'
        };
      }

      // Check if user has permission to update
      const { data: booking, error: fetchError } = await supabase
        .from('bookings')
        .select('user_id, vendor_id')
        .eq('id', bookingId)
        .single();

      if (fetchError || !booking) {
        return {
          success: false,
          error: 'Booking not found'
        };
      }

      // Update booking
      const { data, error } = await supabase
        .from('bookings')
        .update({ 
          status,
          updated_at: new Date().toISOString()
        })
        .eq('id', bookingId)
        .select(`
          *,
          vendor:vendor_profiles!vendor_id (
            id,
            business_name
          ),
          user:user_profiles!user_id (
            id,
            full_name
          )
        `)
        .single();

      if (error) {
        console.error('Error updating booking status:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error in updateBookingStatus:', error);
      return { success: false, error: 'Failed to update booking status' };
    }
  }

  /**
   * Update payment status
   * @param {string} bookingId - Booking UUID
   * @param {string} paymentStatus - New payment status
   * @returns {Promise<Object>} Result with updated booking
   */
  async updatePaymentStatus(bookingId, paymentStatus) {
    try {
      const { data, error } = await supabase
        .from('bookings')
        .update({ 
          payment_status: paymentStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', bookingId)
        .select()
        .single();

      if (error) {
        console.error('Error updating payment status:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error in updatePaymentStatus:', error);
      return { success: false, error: 'Failed to update payment status' };
    }
  }

  /**
   * Cancel a booking
   * @param {string} bookingId - Booking UUID
   * @param {string} userId - User cancelling the booking
   * @returns {Promise<Object>} Result with cancelled booking
   */
  async cancelBooking(bookingId, userId) {
    try {
      // Verify booking exists and user has permission
      const { data: booking, error: fetchError } = await supabase
        .from('bookings')
        .select('user_id, vendor_id, status')
        .eq('id', bookingId)
        .single();

      if (fetchError || !booking) {
        return {
          success: false,
          error: 'Booking not found'
        };
      }

      // Check if booking can be cancelled
      if (booking.status === 'completed') {
        return {
          success: false,
          error: 'Cannot cancel a completed booking'
        };
      }

      if (booking.status === 'cancelled') {
        return {
          success: false,
          error: 'Booking is already cancelled'
        };
      }

      // Update to cancelled
      const { data, error } = await supabase
        .from('bookings')
        .update({ 
          status: 'cancelled',
          updated_at: new Date().toISOString()
        })
        .eq('id', bookingId)
        .select(`
          *,
          vendor:vendor_profiles!vendor_id (
            id,
            business_name
          ),
          user:user_profiles!user_id (
            id,
            full_name
          )
        `)
        .single();

      if (error) {
        console.error('Error cancelling booking:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error in cancelBooking:', error);
      return { success: false, error: 'Failed to cancel booking' };
    }
  }

  /**
   * Update booking details
   * @param {string} bookingId - Booking UUID
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>} Result with updated booking
   */
  async updateBooking(bookingId, updates) {
    try {
      const allowedUpdates = [
        'event_date',
        'event_time',
        'event_location',
        'guest_count',
        'total_amount',
        'special_requirements',
        'package_id'
      ];

      // Filter to only allowed updates
      const filteredUpdates = {};
      Object.keys(updates).forEach(key => {
        if (allowedUpdates.includes(key)) {
          filteredUpdates[key] = updates[key];
        }
      });

      filteredUpdates.updated_at = new Date().toISOString();

      const { data, error } = await supabase
        .from('bookings')
        .update(filteredUpdates)
        .eq('id', bookingId)
        .select(`
          *,
          vendor:vendor_profiles!vendor_id (
            id,
            business_name
          ),
          package:vendor_packages!package_id (
            id,
            name,
            price
          )
        `)
        .single();

      if (error) {
        console.error('Error updating booking:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error in updateBooking:', error);
      return { success: false, error: 'Failed to update booking' };
    }
  }

  /**
   * Get booking statistics for a vendor
   * @param {string} vendorId - Vendor UUID
   * @returns {Promise<Object>} Result with statistics
   */
  async getVendorBookingStats(vendorId) {
    try {
      const { data, error } = await supabase
        .from('bookings')
        .select('status, total_amount, event_date')
        .eq('vendor_id', vendorId);

      if (error) {
        console.error('Error fetching booking stats:', error);
        return { success: false, error: error.message };
      }

      const stats = {
        total: data.length,
        pending: data.filter(b => b.status === 'pending').length,
        confirmed: data.filter(b => b.status === 'confirmed').length,
        completed: data.filter(b => b.status === 'completed').length,
        cancelled: data.filter(b => b.status === 'cancelled').length,
        totalRevenue: data
          .filter(b => b.status === 'completed')
          .reduce((sum, b) => sum + (b.total_amount || 0), 0),
        upcomingEvents: data.filter(b => 
          new Date(b.event_date) > new Date() && 
          b.status === 'confirmed'
        ).length
      };

      return { success: true, data: stats };
    } catch (error) {
      console.error('Error in getVendorBookingStats:', error);
      return { success: false, error: 'Failed to fetch booking statistics' };
    }
  }

  /**
   * Check if user can book a vendor
   * @param {string} userId - User UUID
   * @param {string} vendorId - Vendor UUID
   * @returns {Promise<Object>} Result with eligibility status
   */
  async canUserBook(userId, vendorId) {
    try {
      // Check for existing pending or confirmed bookings
      const { data, error } = await supabase
        .from('bookings')
        .select('id, status')
        .eq('user_id', userId)
        .eq('vendor_id', vendorId)
        .in('status', ['pending', 'confirmed']);

      if (error) {
        console.error('Error checking booking eligibility:', error);
        return { success: false, error: error.message };
      }

      const canBook = data.length === 0;

      return {
        success: true,
        data: {
          canBook,
          reason: canBook ? null : 'You already have a pending or confirmed booking with this vendor'
        }
      };
    } catch (error) {
      console.error('Error in canUserBook:', error);
      return { success: false, error: 'Failed to check booking eligibility' };
    }
  }

  /**
   * Subscribe to booking updates
   * @param {string} bookingId - Booking UUID
   * @param {Function} callback - Callback function for updates
   * @returns {Object} Subscription object
   */
  subscribeToBookingUpdates(bookingId, callback) {
    const channel = supabase
      .channel(`booking-${bookingId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'bookings',
          filter: `id=eq.${bookingId}`
        },
        (payload) => {
          callback(payload.new);
        }
      )
      .subscribe();

    return channel;
  }
}

// Export singleton instance
const bookingService = new BookingService();
export default bookingService;
